import { StarIcon as StarredIcon } from "@heroicons/react/20/solid";
import { StarIcon } from "@heroicons/react/24/outline";
import { useId, useState, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";
import { itemIdOf } from "../../../api/content/processDefinitions";
import type { WorkflowSummary } from "../../../api/content/workflowsApi";
import IconButton from "../../../ui/buttons/IconButton";
import Chip from "../../../ui/feedback/Chip";
import EmptyState from "../../../ui/feedback/EmptyState";
import Field from "../../../ui/forms/Field";
import { classNames } from "../../../ui/styles/classNames";
import { FORM_CONTROL_CLASS } from "../../../ui/styles/formStyles";
import { changePreferences, useUserPreferences } from "../../profile/preferences/userPreferences";
import { namesText } from "../../threads/stepPlans/planText";
import { classText } from "../inputCards/typeWords";
import { useRecentWorkflows } from "./useRecentWorkflows";
import { matchesSearch, searchWords, workflowGroups, type WorkflowGroup } from "./workflowGroups";

interface WorkflowGroupListProps {
  projectId: string;
  workflows: readonly WorkflowSummary[];
  /** One Workflow's row, as this list uses it; `marks` are its origin mark and star, to place in it. */
  row: (workflow: WorkflowSummary, marks: ReactNode) => ReactNode;
  /** The Workflow this list has open or chosen: a folded group that holds it opens. */
  current: string | null;
}

/**
 * Every list of Workflows, searched and grouped one way (F97, F111, F123): a search over names,
 * descriptions and inputs, then the person's favourites, the few used most recently in the
 * project, the project's Workflows, the DMW basis, and the parts of other Workflows, folded away
 * (`workflowGroups`). A star keeps a Workflow among the favourites, in the person's own
 * settings. The Thread view's chooser and the Workflows sidebar each bring only their row: a
 * choice, or a link.
 */
export default function WorkflowGroupList({ projectId, workflows, row, current }: WorkflowGroupListProps) {
  const { t } = useTranslation();
  const searchId = useId();
  const [query, setQuery] = useState("");
  const { preferences, status, saving } = useUserPreferences();
  // Any Workflow of the list may be recent but a starred one, which stays among the favourites.
  const candidates = workflows.map((workflow) => itemIdOf(workflow.reference)).filter((item) => !preferences.favouriteWorkflows.has(item));
  const recent = useRecentWorkflows(projectId, candidates);
  // One star at a time, once the stars are read: each answer replaces the settings shown.
  const starsLocked = status !== "loaded" || saving;
  const words = searchWords(query);
  const shown = workflows.filter((workflow) => matchesSearch(workflow, words));
  const rows = (group: WorkflowGroup) => group.workflows.map((workflow) => row(workflow, (
    <WorkflowMarks workflow={workflow} marked={group.marksOrigin} favourite={preferences.favouriteWorkflows.has(itemIdOf(workflow.reference))} starsLocked={starsLocked} />
  )));
  return (
    <>
      {workflows.length === 0 ? null : (
        <Field id={searchId} labelHidden label={t("workflows.list.search")}>
          {(control) => (
            <input
              {...control}
              type="search"
              value={query}
              placeholder={t("workflows.list.search")}
              className={classNames(FORM_CONTROL_CLASS, "text-sm")}
              onChange={(event) => { setQuery(event.target.value); }}
            />
          )}
        </Field>
      )}
      {workflows.length > 0 && shown.length === 0 ? <EmptyState size="small">{t("workflows.list.noMatch")}</EmptyState> : null}
      {workflowGroups(shown, preferences.favouriteWorkflows, recent, (theme) => classText(theme, t)).map((group) => {
        const title = groupTitle(group, t);
        const key = `${group.name}:${group.kind?.key ?? ""}`;
        // A search opens the folded parts, so a match is never hidden.
        return group.folded ? (
          <details key={key} open={words.length > 0 || group.workflows.some((workflow) => workflow.reference === current)}>
            <summary className="app-title cursor-pointer text-xs font-semibold">{title}</summary>
            <div className="mt-2 space-y-2">{rows(group)}</div>
          </details>
        ) : (
          <section key={key} className="space-y-2" aria-label={title}>
            <h4 className="app-title text-xs font-semibold">{title}</h4>
            {rows(group)}
          </section>
        );
      })}
    </>
  );
}

/** A group's heading: its origin's, and for a work area or a kind its words from the server:
 * the area's name (`classText`, P-20), the general Workflow's name or its class's words (P-19). */
function groupTitle(group: WorkflowGroup, t: TFunction): string {
  const origin = t(`workflows.list.groups.${group.name}`, { count: group.workflows.length });
  const onlyPart = group.name === "parts" && group.workflows.length === 1 ? group.workflows[0] : undefined;
  if (onlyPart !== undefined) return `${origin} · ${onlyPart.name}`;
  if (group.kind === null) {
    return origin;
  }
  const kind = "theme" in group.kind
    ? classText(group.kind.theme, t)
    : "general" in group.kind
      ? group.kind.general.name ?? group.kind.general.reference
      : classText(group.kind.classIri, t);
  return t("workflows.list.groups.kind", { origin, kind });
}

interface WorkflowMarksProps {
  workflow: WorkflowSummary;
  /** Where no group heading says where it comes from: mark the DMW basis or the project. */
  marked: boolean;
  favourite: boolean;
  /** The star waits: settings not read yet, or a change still saving. */
  starsLocked: boolean;
}

/** What a Workflow is part of, its origin mark where its group's heading doesn't say it, and its star.
 * A starred part still says it is one (Sol S3). */
function WorkflowMarks({ workflow, marked, favourite, starsLocked }: WorkflowMarksProps) {
  const { t } = useTranslation();
  const partOf = workflow.partOf.map((parent) => parent.name);
  return (
    <>
      {partOf.length === 0 ? null : <Chip size="compact" className="max-w-full">{t("workflows.list.partOf", { workflows: namesText(partOf, t) })}</Chip>}
      {marked ? <Chip size="compact">{t(workflow.fromBasis ? "workflows.list.groups.basis" : "workflows.list.groups.project")}</Chip> : null}
      <IconButton
        className="p-0.5"
        label={t(favourite ? "workflows.list.unstar" : "workflows.list.star", { workflow: workflow.name })}
        aria-pressed={favourite}
        disabled={starsLocked}
        onClick={(event) => {
          event.preventDefault();
          void changePreferences({ favouriteWorkflow: { workflow: itemIdOf(workflow.reference), favourite: !favourite } });
        }}
      >
        {favourite ? <StarredIcon className="h-4 w-4 text-amber-500" aria-hidden="true" /> : <StarIcon className="h-4 w-4" aria-hidden="true" />}
      </IconButton>
    </>
  );
}
